import { Button } from '@davidlloydleisure/common-js-frontend';
import classNames from 'classnames';
import React, { forwardRef, use } from 'react';

import { isAbbyChatLink } from 'src/components/common/abby-chat/abby-chat-link';
import { WebsiteLink } from 'src/components/common/website-link';
import { WebsiteLinkButtonContent } from 'src/components/common/website-link-button-content';
import { shouldUseClubRedesignForClub } from 'src/content/club-look-ups-by-id';
import type { EnabledPages } from 'src/content/enabled-pages-helper';
import { SiteContext } from 'src/context/site-context';
import type { RedesignButtonStyle } from 'src/enums/button-style';
import abby from 'src/helpers/abby';
import { getBrandForBuildType } from 'src/helpers/brand-for-build-type';
import { buttonStyles } from 'src/helpers/button-styles';
import type { ClubPageFeatures, NewClubPageFeatures } from 'src/models/cms/club-page-features';
import type { StaticTextLookupKey } from 'src/translators/static-text-lookup';
import { useStaticTextTranslator } from 'src/translators/static-text-translator';

import { getRedesignButtonStyleClasses } from './buttonCss';
import { findFacilityFromUrlPath, isDisabledFacility } from './facilities';
import type { FacilityDetails } from './facilities';

type ButtonBaseProps = {
  buttonText?: string | null;
  buttonKey?: StaticTextLookupKey;
  buttonLink?: string | null;
  buttonClassName?: string;
  buttonEndIcon?: React.ReactElement;
  buttonType?: string;
  showButton?: boolean;
  gtmTrackingId?: string;
  openInNewTab?: boolean;
  forceOpenInSameTab?: boolean;
};

export type ButtonDetails = ButtonBaseProps & {
  buttonStyle?: Queries.ButtonStyle | null;
  showButton?: boolean;
};

export type CustomHeroButtonsProps = {
  primaryButton?: ButtonDetails;
  secondaryButton?: ButtonDetails;
  useCustomHeroButtons?: boolean;
};

type ClubLinkButtonContentProps = ButtonBaseProps & {
  features: NewClubPageFeatures | ClubPageFeatures;
  enabledPages: EnabledPages;
  className?: string;
  contentClassName?: string;
  buttonStyle?: Queries.ButtonStyle | null;
  isRedesignButton?: boolean;
  handleClick?: (event?: React.MouseEvent<HTMLElement>) => void;
};

const ClubLinkButtonContent = forwardRef<HTMLElement, ClubLinkButtonContentProps>(
  (
    {
      buttonText,
      buttonKey,
      buttonLink,
      buttonClassName,
      buttonEndIcon,
      buttonType,
      gtmTrackingId,
      features,
      enabledPages,
      className = '',
      contentClassName,
      buttonStyle,
      isRedesignButton,
      openInNewTab,
      forceOpenInSameTab,
      handleClick,
    },
    ref
  ) => {
    const translate = useStaticTextTranslator();
    const { language, siteId, buildType } = use(SiteContext);
    const brand = getBrandForBuildType(buildType);
    const { main, wrapper } = getRedesignButtonStyleClasses(buttonStyle, buildType);
    const buttonShouldNotRender =
      (!buttonText && !buttonKey) ||
      !buttonLink ||
      (buttonLink === '/timetable/' && !features.timetable);

    if (buttonShouldNotRender) {
      return null;
    }

    const facility: FacilityDetails | undefined = findFacilityFromUrlPath(buttonLink, language);

    if (
      !shouldUseClubRedesignForClub(siteId) &&
      facility &&
      isDisabledFacility(enabledPages, facility)
    ) {
      return null;
    }

    const content = (
      <WebsiteLinkButtonContent
        buttonClassName={classNames(main, buttonClassName)}
        buttonKey={buttonKey}
        buttonText={buttonText}
        buttonEndIcon={buttonEndIcon}
        buttonType={buttonType}
        contentClassName={contentClassName}
        gtmTrackingId={gtmTrackingId}
      />
    );

    const tabIndexProp = isRedesignButton ? { tabIndex: 0 } : {};

    if (isAbbyChatLink(buttonLink)) {
      return (
        <Button
          brand={brand}
          onClick={() => {
            abby.open();
            handleClick?.();
          }}
          text={buttonText ? buttonText : translate(buttonKey!)}
          style={buttonStyles(buttonStyle as RedesignButtonStyle)}
          focusBorderClassName={classNames(className, 'abby-chat-link')}
          endIcon={buttonEndIcon}
          className={className}
        />
      );
    }

    const linkClassNames = classNames(isRedesignButton && wrapper, className);

    if (
      buttonLink.includes('http') ||
      buttonLink.includes('localhost') ||
      buttonLink.includes('mailto:') ||
      openInNewTab
    ) {
      return (
        <a
          target={forceOpenInSameTab ? '_self' : '_blank'}
          rel={forceOpenInSameTab ? undefined : 'noopener noreferrer'}
          href={buttonLink}
          ref={ref as React.ForwardedRef<HTMLAnchorElement>}
          className={linkClassNames}
          onClick={(event) => handleClick?.(event)}
          {...tabIndexProp}
        >
          {content}
        </a>
      );
    }

    return (
      <WebsiteLink
        to={buttonLink || '/'}
        className={linkClassNames}
        isTabIndex={isRedesignButton}
        forceOpenInSameTab={forceOpenInSameTab}
        handleClick={handleClick}
        ref={ref}
      >
        {content}
      </WebsiteLink>
    );
  }
);

ClubLinkButtonContent.displayName = 'ClubLinkButtonContent';

export default ClubLinkButtonContent;
